"use client";
import { ChangeEvent, useEffect, useState } from "react";
import {
  HydratedInterface,
  getMessageFromNetworkError,
  useRenameInterface,
} from "@zapier/client";

import {
  Button,
  Field,
  ModalDialogBody,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogRoot,
  Text,
  TextInput,
} from "@zapier/design-system";

import { useNotification } from "@zapier/utils";
import { useEmitInterfaceRenameModalEvent } from "@zapier/shared/src/events";
import { useAutoSelectInputText } from "@zapier/shared";
import { useTranslations } from "@zapier/i18n";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  interfaceObj: HydratedInterface | undefined;
  folderId?: string;
}

const ALLOWED_NAME_REGEX = /^[A-Za-z0-9 -]+$/;

export function InterfaceRenameModal({
  isOpen,
  onClose,
  interfaceObj,
  folderId,
}: Props) {
  const nameInputIdentifier = "rename-interface-name";
  const [name, setName] = useState(interfaceObj?.name ?? "");
  const t = useTranslations("interface-rename-modal");
  const { createNotification } = useNotification();
  const { rename, isLoading } = useRenameInterface({ folderId });
  const { emit } = useEmitInterfaceRenameModalEvent();

  useEffect(() => {
    if (!isOpen) return;
    setName(interfaceObj?.name ?? "");
  }, [interfaceObj?.name, isOpen]);

  useAutoSelectInputText({
    isOpen,
    inputSelector: `#${nameInputIdentifier}`,
    value: interfaceObj?.name,
  });

  const handleCancel = () => {
    emit("cancel");
    onClose();
  };

  const trimmedName = name.trim();
  const hasInvalidCharacters =
    Boolean(trimmedName) && !ALLOWED_NAME_REGEX.test(trimmedName);

  const validationError = hasInvalidCharacters
    ? t("validation.invalid-name")
    : undefined;

  const handleSubmit = () => {
    if (!interfaceObj) {
      return;
    }

    if (!trimmedName || trimmedName === interfaceObj.name) {
      // Should not be possible due to disabled button, but just in case
      handleCancel();
      return;
    }

    if (validationError) {
      return;
    }

    rename(
      { id: interfaceObj.id, name: trimmedName },
      {
        onSuccess: () => {
          createNotification({
            message: (
              <Text>
                {t("notifications.rename-success", {
                  oldName: interfaceObj.name,
                  newName: trimmedName,
                })}
              </Text>
            ),
            type: "success",
          });
        },
        onError: (error) => {
          createNotification({
            title: t("notifications.rename-error", {
              interfaceName: interfaceObj.name,
            }),
            message: getMessageFromNetworkError(error),
            type: "error",
          });
        },
      },
    );

    emit("confirm");
    onClose();
  };

  if (!isOpen || !interfaceObj) {
    return;
  }

  const isSubmitDisabled =
    !trimmedName ||
    trimmedName === interfaceObj.name ||
    isLoading ||
    Boolean(validationError);

  return (
    <ModalDialogRoot
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          handleCancel();
        }
      }}
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          handleSubmit();
        }}
      >
        <ModalDialogHeader textAlign="start">
          {t("headings.rename-interface", { interfaceName: interfaceObj.name })}
        </ModalDialogHeader>

        <ModalDialogBody textAlign="start">
          <Field
            label={t("labels.name")}
            inputId={nameInputIdentifier}
            isRequired
            error={validationError}
            renderInput={(inputProps) => (
              <TextInput
                {...inputProps}
                // eslint-disable-next-line jsx-a11y/no-autofocus -- intentionally focus the input when the modal opens
                autoFocus
                id={nameInputIdentifier}
                value={name}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  setName(event.target.value)
                }
                placeholder={t("placeholder")}
                isFullWidth
              />
            )}
          />
        </ModalDialogBody>

        <ModalDialogFooter>
          <Button
            variant="secondary"
            type="button"
            onPress={handleCancel}
            isDisabled={isLoading}
          >
            {t("buttons.cancel")}
          </Button>
          <Button type="submit" isDisabled={isSubmitDisabled}>
            {t("buttons.save")}
          </Button>
        </ModalDialogFooter>
      </form>
    </ModalDialogRoot>
  );
}
